// exclusionWindow — 「人を外して戻す」で絵ログを読む範囲の決め方 (pure)
// (exclusion-ui-investigation-2026-10-10.md「③-b: G を土台にしたプレビューと G 以降だけの DL」)。
//
// 絵ログは**新しい側から必要なぶんだけ**読む (`infrastructure/timelapse/strokeLogWindow.ts`)。
// ここは「どこまで要るか」を `LowerNeed` の列で返す。実際に読むかどうか (すでに足りているか) は
// 窓が `isCovered` で判定し、足りない最初の `LowerNeed` に対して 1 ラウンド読み足す。
//
// 要るもの (優先順):
//   1. 根まで (fallback の全 replay が必要なとき)
//   2. 「もっと前を読む」で利用者が広げた分
//   3. 初期の範囲 = 最後の描画から 2 時間 (印の有無で変えない)。
//        最新イベントの 2 時間前を含むセグメントまで (+ 余白 1 本)
//   4. 途中から切れている可能性のある接続のうち、チェックが入った / 印が付いたもの:
//      もう 1 ラウンド読み足す (切れていなくなるまで繰り返す)
//   5. 土台の世代が窓の中に見つからない (`no_base_generation`) のに窓が根まで届いていない:
//      もう 1 ラウンド読み足す
//
// **切れている可能性のある接続** (`findPossiblyCutUids`) の判定 (窓が根まで届いていないときだけ):
//   - その接続の最初のイベントが、読んだ最も古いセグメントの中にある (それより前にも
//     イベントがあるかもしれない)
//   - その接続の `stroke.segment` / `stroke.end` で、窓の中に `stroke.start` が無い sid がある
//     (線の途中から読み始めている = 少なくとも 1 本は切れている)
// どちらも「全部読まないと確実には分からない」ものの目安。接続が長く idle を挟んで描いていると
// 見逃すが、その場合は「もっと前を読む」で補う。見逃した接続を外すと G が本来より新しくなり、
// 荒らしの線が G に焼き込まれたままになるので、G を決める前に切れた接続を読み切る。

import type { StrokeLogEvent } from '@oekaki/protocol';
import type { LowerNeed } from '../infrastructure/timelapse/strokeLogWindow.js';
import type { ConnectionSummary } from './exclusionCandidates.js';

/** 最初に読む時間 (最新イベントからさかのぼる)。 */
export const EXCLUSION_RECENT_MS = 2 * 60 * 60_000;
/** 「もっと前を読む」1 回で広げる時間。 */
export const EXCLUSION_EXTEND_MS = 2 * 60 * 60_000;

/** 切れている可能性のある接続の uid。窓が根まで届いている (`truncated: false`) なら空。 */
export const findPossiblyCutUids = (input: {
  readonly summary: ConnectionSummary;
  /** 系譜に絞った (外す前の) イベント列。 */
  readonly scoped: readonly StrokeLogEvent[];
  readonly truncated: boolean;
  /** 読んだ最も古いセグメントの toSeq。 */
  readonly floorToSeq: number | null;
}): ReadonlySet<string> => {
  const out = new Set<string>();
  if (!input.truncated || input.floorToSeq === null) return out;
  const floorTo = input.floorToSeq;
  for (const c of input.summary.connections) {
    if (c.firstSeq <= floorTo) out.add(c.uid);
  }
  const started = new Set<string>();
  for (const ev of input.scoped) if (ev.t === 'stroke.start') started.add(ev.sid);
  for (const ev of input.scoped) {
    if ((ev.t === 'stroke.segment' || ev.t === 'stroke.end') && !started.has(ev.sid)) {
      out.add(ev.uid);
    }
  }
  return out;
};

export interface DecideNeedsInput {
  /** 根まで読む必要がある (fallback の全 replay)。 */
  readonly full: boolean;
  /** 「もっと前を読む」で利用者が広げた分 (積み上げ)。 */
  readonly userNeeds: readonly LowerNeed[];
  readonly summary: ConnectionSummary;
  /** 窓の最新イベントの ts。イベントが無ければ null。 */
  readonly lastEventTs: number | null;
  readonly truncated: boolean;
  readonly cutUids: ReadonlySet<string>;
  /** 「外す」にチェックが入っている uid。 */
  readonly checkedUids: ReadonlySet<string>;
  /** G が窓の中に見つからなかった (`no_base_generation`)。 */
  readonly noBaseGeneration: boolean;
}

/** 窓が満たすべき下端の要求 (優先順)。窓の `isCovered` が最初に false のものを読み足す。 */
export const decideLowerNeeds = (input: DecideNeedsInput): readonly LowerNeed[] => {
  if (input.full) return [{ kind: 'all' }];
  const out: LowerNeed[] = [...input.userNeeds];
  if (input.lastEventTs !== null) {
    out.push({ kind: 'ts', ts: input.lastEventTs - EXCLUSION_RECENT_MS });
  }
  if (input.truncated) {
    const marked = new Set(
      input.summary.connections.filter((c) => c.marks.length > 0).map((c) => c.uid),
    );
    for (const uid of input.cutUids) {
      if (input.checkedUids.has(uid) || marked.has(uid)) {
        out.push({ kind: 'more' });
        break;
      }
    }
    if (input.noBaseGeneration) out.push({ kind: 'more' });
  }
  return out;
};

/** 「もっと前を読む」: いま読んでいる範囲の先頭から更に `EXCLUSION_EXTEND_MS` 前まで。 */
export const extendNeed = (startTs: number): LowerNeed => ({
  kind: 'ts',
  ts: startTs - EXCLUSION_EXTEND_MS,
});

/** 窓が持つイベントのうち最も古い ts。 */
export const windowStartTs = (events: readonly StrokeLogEvent[]): number | null => {
  let min: number | null = null;
  for (const ev of events) if (min === null || ev.ts < min) min = ev.ts;
  return min;
};

/** 切れた接続を読み足している間に画面へ出す、対象の名前 (最初の 1 件)。 */
export const firstNeededCut = (input: {
  readonly summary: ConnectionSummary;
  readonly cutUids: ReadonlySet<string>;
  readonly checkedUids: ReadonlySet<string>;
}): string | null => {
  for (const c of input.summary.connections) {
    if (!input.cutUids.has(c.uid)) continue;
    if (input.checkedUids.has(c.uid) || c.marks.length > 0) {
      return c.displayNames.length > 0 ? c.displayNames.join(' → ') : '(名前なし)';
    }
  }
  return null;
};
